import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import { CircularProgress, IconButton, Paper, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";

import type {
	SectionSpecFileChange,
	SectionSpecRotation,
} from "../../models/SectionSpecRotation";
import { SectionSpecRotateButtons } from "./SectionSpecRotateButtons";

export interface SectionSpecRotationState {
	canRotate: boolean;
	change: SectionSpecFileChange | null;
	rotating: boolean;
	saving: boolean;
	rotate: (rotation: SectionSpecRotation) => void;
	save?: () => void;
	discard?: () => void;
}

export const ROTATION_CONTROLS_HOVER_SX = {
	"&:hover [data-rotation-controls], &:focus-within [data-rotation-controls]": {
		opacity: 1,
	},
} as const;

interface SectionSpecRotationControlsProps {
	label: string;
	rotation: SectionSpecRotationState;
}

export const SectionSpecRotationControls = ({
	label,
	rotation,
}: SectionSpecRotationControlsProps) => {
	if (!rotation.canRotate) return null;

	const { save, discard } = rotation;
	const isPending = rotation.change !== null && save !== undefined;

	return (
		<Paper
			data-rotation-controls
			elevation={2}
			sx={{
				alignItems: "center",
				backdropFilter: "blur(4px)",
				backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.85),
				borderRadius: 1,
				display: "flex",
				gap: 0.25,
				opacity: isPending ? 1 : 0,
				position: "absolute",
				px: 0.25,
				right: 8,
				top: 8,
				transition: "opacity 150ms",
				zIndex: 2,
				"@media (hover: none)": { opacity: 1 },
			}}
		>
			{isPending && (
				<>
					{discard && (
						<Tooltip title="Discard rotation">
							<span>
								<IconButton
									aria-label={`Discard ${label} rotation`}
									size="small"
									disabled={rotation.saving}
									onClick={discard}
								>
									<UndoIcon fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					)}
					<Tooltip title="Save rotation">
						<span>
							<IconButton
								aria-label={`Save ${label} rotation`}
								size="small"
								color="primary"
								disabled={rotation.rotating || rotation.saving}
								onClick={save}
							>
								{rotation.saving ? (
									<CircularProgress size={16} />
								) : (
									<CheckIcon fontSize="small" />
								)}
							</IconButton>
						</span>
					</Tooltip>
				</>
			)}
			<SectionSpecRotateButtons
				label={label}
				disabled={rotation.saving}
				rotating={rotation.rotating}
				onRotate={rotation.rotate}
			/>
		</Paper>
	);
};
